import { useState } from "react";
import cx from "classnames";

import LoyaltyProgramMarketingBanners from "client/loyalty_programs/components/LoyaltyProgramMarketingBanners";
import PillButton from "client/common/components/buttons/PillButton";
import HorizontalOverflow from "client/common/components/display/HorizontalOverflow";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import FetchErrorContainer from "client/common/components/errors/FetchErrorContainer";
import ProductLineCard from "client/products/components/ProductLineCard";
import { TopSellersTitle } from "client/products/components/CarouselSections";
import { useBrowseQuery } from "client/products/hooks";
import CategoryCard, { DEALS_CATEGORY } from "client/products/components/CategoryCard";
import {
  IBrowseFilterSpeciesKind,
  IClientBrowseCategory,
  BrowseAllPatientKind,
  BrowseProductLine,
} from "client/products/types";
import { urls } from "client/routes";
import { useProductLinesViewOnVisible, useViewItemListOnVisible } from "client/analytics/utils";
import { ProductLineListSource } from "client/analytics/types";
import { Clinic } from "client/clinics/types";
import { useLoyaltyProgramBannersQuery } from "client/loyalty_programs/hooks";

import CatalogFilters from "./CatalogFilters";
import DealsMarketingBanner from "./DealsMarketingBanner";

import styles from "../styles/HomeBrowse.module.less";

type CategoryCarouselProps = {
  categories: IClientBrowseCategory[];
};

export const CategoryCarousel = ({ categories }: CategoryCarouselProps) => {
  return (
    <HorizontalOverflow
      direction="right"
      classNames={{
        scroller: styles.categories,
      }}
    >
      <CategoryCard.Small category={DEALS_CATEGORY} />
      {categories.map((category) => (
        <CategoryCard.Small key={category.id} category={category} />
      ))}
    </HorizontalOverflow>
  );
};

type FeaturedSectionProps = {
  productLines: BrowseProductLine[];
  isPersonalized: boolean;
};

const FeaturedSection = ({ productLines, isPersonalized }: FeaturedSectionProps) => {
  const eventViewName = isPersonalized
    ? ProductLineListSource.HomePageForYou
    : ProductLineListSource.HomePageFeatured;
  const ref = useViewItemListOnVisible(productLines, eventViewName);
  const productLinesViewRef = useProductLinesViewOnVisible(productLines, eventViewName);

  return (
    <div className={styles.category}>
      <div className={styles.info}>
        <div className={styles.titles}>
          <TopSellersTitle
            isPersonalized={isPersonalized}
            className={styles.title}
            accentClassName={styles.accent}
          />
        </div>
      </div>
      <HorizontalOverflow
        ref={ref}
        direction="right"
        classNames={{ scroller: styles.products, button: styles.button }}
      >
        <CategoryCard.TopSellers className={styles.card} isPersonalized={isPersonalized} />
        {productLines.map((productLine, index) => (
          <div
            key={productLine.id}
            ref={index === 0 ? productLinesViewRef : undefined}
            className={styles.card}
          >
            <ProductLineCard
              productLine={productLine}
              eventViewName={eventViewName}
              eventCategoryId={null}
            />
          </div>
        ))}
      </HorizontalOverflow>
    </div>
  );
};

type CategorySectionProps = {
  category: IClientBrowseCategory;
};

const CategorySection = ({ category }: CategorySectionProps) => {
  const itemListRef = useViewItemListOnVisible(
    category.product_lines,
    `${ProductLineListSource.HomePageCategory} - ${category.name}`,
  );
  const productLinesViewRef = useProductLinesViewOnVisible(
    category.product_lines,
    ProductLineListSource.HomePageCategory,
    category.id,
  );

  return (
    <div className={styles.category}>
      <div className={styles.info}>
        <div className={styles.titles}>
          <h3 className={styles.title}>{category.extended_name}</h3>
        </div>
        <PillButton
          size="s"
          variant="secondary"
          dest={urls.products.category({ params: { id: category.id } })}
        >
          View All
        </PillButton>
      </div>
      <HorizontalOverflow
        ref={itemListRef}
        direction="right"
        classNames={{ scroller: styles.products, button: styles.button }}
      >
        <CategoryCard category={category} className={styles.card} />
        {category.product_lines.map((productLine, index) => (
          <div
            key={productLine.id}
            ref={index === 0 ? productLinesViewRef : undefined}
            className={styles.card}
          >
            <ProductLineCard
              productLine={productLine}
              eventViewName={ProductLineListSource.HomePageCategory}
              eventCategoryId={category.id}
            />
          </div>
        ))}
      </HorizontalOverflow>
    </div>
  );
};

type HomeBrowseContentProps = {
  species: IBrowseFilterSpeciesKind;
};

const HomeBrowseContent = ({ species }: HomeBrowseContentProps) => {
  const {
    data: browse,
    isLoading: isBrowseLoading,
    error: browseError,
  } = useBrowseQuery({ species }, { enabled: true });
  const { data: loyaltyProgramBanners, isLoading: isLoyaltyProgramBannersLoading } =
    useLoyaltyProgramBannersQuery();

  if (isBrowseLoading || isLoyaltyProgramBannersLoading) {
    return <LoadingContainer />;
  }

  if (browseError || !browse) {
    return <FetchErrorContainer error={browseError} entityName="categories" isPlural={true} />;
  }

  const shouldShowLoyaltyProgramBanners = loyaltyProgramBanners && loyaltyProgramBanners.length > 0;

  return (
    <div
      className={cx(
        styles.homeBrowseContent,
        shouldShowLoyaltyProgramBanners && styles.loyaltyProgramBanners,
      )}
    >
      <CategoryCarousel categories={browse.categories} />
      {shouldShowLoyaltyProgramBanners && (
        <LoyaltyProgramMarketingBanners banners={loyaltyProgramBanners} />
      )}
      {!loyaltyProgramBanners?.length && <DealsMarketingBanner />}
      <div className={styles.categorySections}>
        <FeaturedSection
          productLines={browse.featured_product_lines}
          isPersonalized={browse.is_personalized}
        />
        {browse.categories.map((category) => (
          <CategorySection key={category.id} category={category} />
        ))}
      </div>
    </div>
  );
};

type HomeBrowseHeaderProps = {
  species: IBrowseFilterSpeciesKind;
  setSpecies: (species: IBrowseFilterSpeciesKind) => void;
  clinic: Clinic;
};

const HomeBrowseHeader = ({ species, setSpecies, clinic }: HomeBrowseHeaderProps) => {
  return (
    <div className={styles.heading}>
      <div className={styles.header}>
        Search by our <span className={styles.accent}>popular categories</span>
      </div>
      <div className={styles.actions}>
        <CatalogFilters species={species} setSpecies={setSpecies} clinic={clinic} />
      </div>
    </div>
  );
};

const HomeBrowseV1 = ({ clinic }: { clinic: Clinic }) => {
  const [species, setSpecies] = useState<IBrowseFilterSpeciesKind>(BrowseAllPatientKind.ALL);
  return (
    <div className={styles.homeBrowse}>
      <HomeBrowseHeader species={species} setSpecies={setSpecies} clinic={clinic} />
      <HomeBrowseContent species={species} />
    </div>
  );
};

export default HomeBrowseV1;
